import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { APP_VERSION } from "@/version";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600 }}>🌿 Privacy Policy</h2>
        <Button size="sm" onClick={() => navigate(-1)} data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="dashboard-content" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem' }}>Privacy Policy</h1>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.9rem' }}>Cilantro Alert {APP_VERSION} — Last updated: February 2026</p>
        </div>

        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader><CardTitle>1. Introduction</CardTitle></CardHeader>
          <CardContent>
            <p style={{ lineHeight: '1.7', color: '#444' }}>
              Cilantro Alert ("we", "us", or "our") is operated by RM-Worx BV (rudy@rm-worx.be).
              This privacy policy explains how we collect, use, and protect your information when you use our application.
            </p>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader><CardTitle>2. Information We Collect</CardTitle></CardHeader>
          <CardContent>
            <div style={{ lineHeight: '1.7', color: '#444' }}>
              <p style={{ marginBottom: '0.75rem' }}>We collect the following information when you create an account:</p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '0.75rem' }}>
                <li><strong>Name and email address</strong> — provided via your chosen login provider (Google, GitHub, or Facebook)</li>
                <li><strong>Profile picture</strong> — provided by your login provider (optional)</li>
                <li><strong>Product contributions</strong> — barcodes, product names, and cilantro status you submit</li>
              </ul>
              <p>We do <strong>not</strong> collect payment information, location data, or device identifiers.</p>
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader><CardTitle>3. How We Use Your Information</CardTitle></CardHeader>
          <CardContent>
            <div style={{ lineHeight: '1.7', color: '#444' }}>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li>To provide and maintain the Cilantro Alert service</li>
                <li>To display your name in the app when you are logged in</li>
                <li>To associate product contributions with your account</li>
                <li>To prevent abuse and maintain the integrity of the database</li>
              </ul>
              <p style={{ marginTop: '0.75rem' }}>We <strong>do not</strong> sell, trade, or share your personal data with third parties for marketing purposes.</p>
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader><CardTitle>4. Authentication</CardTitle></CardHeader>
          <CardContent>
            <p style={{ lineHeight: '1.7', color: '#444' }}>
              We use <strong>Auth0</strong> as our authentication provider. When you log in via Google, GitHub, or Facebook,
              those providers share basic profile information with Auth0, which then provides us with your identity.
              We do not store your passwords. Please review Auth0's privacy policy at{' '}
              <a href="https://auth0.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a' }}>auth0.com/privacy</a>.
            </p>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader><CardTitle>5. Data Storage</CardTitle></CardHeader>
          <CardContent>
            <p style={{ lineHeight: '1.7', color: '#444' }}>
              Your data is stored in a secure MongoDB Atlas database hosted in the EU (Frankfurt region).
              Authentication tokens are stored locally on your device using encrypted local storage.
            </p>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader><CardTitle>6. Your Rights</CardTitle></CardHeader>
          <CardContent>
            <div style={{ lineHeight: '1.7', color: '#444' }}>
              <p style={{ marginBottom: '0.75rem' }}>Under GDPR, you have the right to:</p>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your account and associated data</li>
                <li>Data portability</li>
              </ul>
              <p style={{ marginTop: '0.75rem' }}>
                To exercise these rights, contact us at{' '}
                <a href="mailto:rudy@rm-worx.be" style={{ color: '#16a34a' }}>rudy@rm-worx.be</a>.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader><CardTitle>7. Cookies</CardTitle></CardHeader>
          <CardContent>
            <p style={{ lineHeight: '1.7', color: '#444' }}>
              We use local storage (not cookies) to maintain your login session. No tracking or advertising cookies are used.
            </p>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader><CardTitle>8. Contact</CardTitle></CardHeader>
          <CardContent>
            <p style={{ lineHeight: '1.7', color: '#444' }}>
              For any privacy-related questions, contact:<br />
              <strong>RM-Worx BV</strong><br />
              Ondernemingsnummer / VAT: BE 1041.491.473<br />
              <a href="mailto:rudy@rm-worx.be" style={{ color: '#16a34a' }}>rudy@rm-worx.be</a><br />
              <a href="https://www.rm-worx.be" target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a' }}>www.rm-worx.be</a>
            </p>
          </CardContent>
        </Card>

        <div style={{ textAlign: 'center', marginTop: '2rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
          <p>© 2026 RM-Worx BV. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
